/* ==========================================================================
   Terlato Kitchen — "Find Their Gift" · shared client store
   window.GFFinder

   One job: hold the visitor's three answers and tell anyone who cares when they
   change. It owns no markup and does no matching. The console section
   (gf-finder-console) writes answers into it; the result section
   (gf-finder-result) reads them out and decides which product to show. Keeping
   the store between them is what lets the two live as separate Shopify sections
   a merchant can reorder, remove or drop onto another page.

   Deliberately NOT persisted. A gift finder answered last week is worse than an
   empty one — the occasion has passed. State lives for the page view, plus the
   query string below so a result can be shared or reloaded.

   Query string: ?recipient=for-her&occasion=thank-you&comfort_level=50-100
   Written on submit with replaceState (no history entry per answer), read back
   on load. Keys are the question keys a merchant sets in the theme editor, so a
   renamed question simply stops matching an old link rather than breaking.

   Progressive enhancement: nothing here is required for the page to render. The
   console ships real radio inputs inside a real <form>; with this script absent
   they still tick, and the form's own action lands the visitor on the fallback
   collection.
   ========================================================================== */

(function (window, document) {
  'use strict';

  if (window.GFFinder) return;

  /* ---------------------------------------------------------------------
     State
     --------------------------------------------------------------------- */

  var questions = []; // [{ key, label, prompt }] in step order
  var answers = {}; // { questionKey: { value, label } }
  var submitted = false;
  var listeners = [];

  function questionIndex(key) {
    for (var i = 0; i < questions.length; i++) {
      if (questions[i].key === key) return i;
    }
    return -1;
  }

  function snapshot(reason) {
    /* Fresh copies out. A subscriber that mutates what it is handed must not be
       able to reach back into the store. */
    var out = { reason: reason || 'change', submitted: submitted, questions: [], answers: {}, complete: true };

    for (var i = 0; i < questions.length; i++) {
      var q = questions[i];
      var a = answers[q.key];
      out.questions.push({ key: q.key, label: q.label, prompt: q.prompt });
      if (a) {
        out.answers[q.key] = { value: a.value, label: a.label };
      } else {
        out.complete = false;
      }
    }

    /* No questions registered at all is not "complete" — it is a page with no
       console on it, and a result section should stay quiet rather than claim a
       match nobody asked for. */
    if (!questions.length) out.complete = false;

    return out;
  }

  function notify(reason) {
    var state = snapshot(reason);
    for (var i = 0; i < listeners.length; i++) {
      try {
        listeners[i](state);
      } catch (err) {
        /* One broken subscriber must not stop the others, and must not take the
           page down with it. */
        if (window.console && window.console.error) window.console.error(err);
      }
    }
  }

  /* ---------------------------------------------------------------------
     Query string
     --------------------------------------------------------------------- */

  function readUrl() {
    var out = {};
    var search = window.location.search;
    if (!search || search.length < 2) return out;

    var parts = search.slice(1).split('&');
    for (var i = 0; i < parts.length; i++) {
      if (!parts[i]) continue;
      var pair = parts[i].split('=');
      var key = decodeURIComponent(pair[0].replace(/\+/g, ' '));
      var value = pair.length > 1 ? decodeURIComponent(pair[1].replace(/\+/g, ' ')) : '';
      if (key && value) out[key] = value;
    }
    return out;
  }

  function writeUrl() {
    if (!window.history || !window.history.replaceState) return;

    var params = new URLSearchParams(window.location.search);
    for (var i = 0; i < questions.length; i++) {
      var a = answers[questions[i].key];
      if (a) params.set(questions[i].key, a.value);
      else params.delete(questions[i].key);
    }
    var url = window.location.pathname + (params.toString() ? '?' + params.toString() : '') + window.location.hash;
    try {
      window.history.replaceState(window.history.state, '', url);
    } catch (err) {
      /* Sandboxed iframes (the theme editor preview among them) can refuse
         replaceState. A shareable URL is a nicety; losing it changes nothing. */
    }
  }

  /* ---------------------------------------------------------------------
     Public API
     --------------------------------------------------------------------- */

  var GFFinder = {
    /* Called once per question by the console as it renders. Re-registering the
       same key updates the labels rather than adding a duplicate, so a section
       re-render in the theme editor does not double the stepper. */
    registerQuestion: function (question) {
      if (!question || !question.key) return;
      var at = questionIndex(question.key);
      var entry = {
        key: question.key,
        label: question.label || question.key,
        prompt: question.prompt || ''
      };
      if (at === -1) {
        questions.push(entry);
      } else {
        questions[at] = entry;
      }
    },

    /* The console calls this after registering, so answers can be restored from
       a shared link. Returns the raw map so the console can tick the matching
       radio itself — the store never touches DOM. */
    answersFromUrl: readUrl,

    setAnswer: function (key, value, label) {
      if (!key) return;
      if (value === null || value === undefined || value === '') {
        delete answers[key];
      } else {
        answers[key] = { value: String(value), label: label ? String(label) : String(value) };
      }
      if (submitted) writeUrl();
      notify('answer');
    },

    getAnswer: function (key) {
      var a = answers[key];
      return a ? { value: a.value, label: a.label } : null;
    },

    getState: function () {
      return snapshot('read');
    },

    isComplete: function () {
      return snapshot('read').complete;
    },

    /* "The visitor asked for their match." Separate from setAnswer so the result
       band only appears when it was requested, never mid-way through answering. */
    submit: function () {
      if (!snapshot('read').complete) {
        notify('incomplete');
        return false;
      }
      submitted = true;
      writeUrl();
      notify('submit');
      return true;
    },

    reset: function () {
      answers = {};
      submitted = false;
      writeUrl();
      notify('reset');
    },

    subscribe: function (fn) {
      if (typeof fn !== 'function') return function () {};
      listeners.push(fn);
      /* Hand the newcomer the current state straight away, so a section that
         boots after an answer was restored from the URL is not a frame behind. */
      fn(snapshot('subscribe'));
      return function () {
        var at = listeners.indexOf(fn);
        if (at !== -1) listeners.splice(at, 1);
      };
    },

    /* Shared by both sections so "jump to the result" behaves the same in each,
       and so neither has to re-derive the reduced-motion rule. */
    scrollTo: function (el) {
      if (!el || !el.scrollIntoView) return;
      var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      try {
        el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      } catch (err) {
        el.scrollIntoView();
      }
    }
  };

  window.GFFinder = GFFinder;
})(window, document);
